<template>
  <v-row dense>
    <!-- ① เลือกตัวถอดเสียง — ส่วนแรกของแท็บ · ค่าที่ใช้ร่วมกันทั้งสองตัวมีแค่สวิตช์เปิดรับ stream
         ⚠️ ทุกช่องมีคู่แฝดใน VoiceStreamSettings ฝั่ง middleware — ค่าตั้งต้น/ช่วง
            (STT_DEFAULTS / GATE_FIELDS ข้างล่าง) ต้องตรงกับ QaVoiceStreamModels.cs
         ⚠️ ไม่มีช่องกรอก API Key ที่นี่ และห้ามเพิ่ม — คีย์อยู่ที่ configuration ฝั่ง middleware (OwnStt:ApiKey) -->
    <v-col cols="12">
      <v-card class="qa-card-sm" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaVsPickTitle') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <div class="text-caption text-medium-emphasis mb-3">{{ $t('qaVsProvDesc') }}</div>
          <div class="qa-vs-tiles" role="radiogroup" :aria-label="$t('qaVsPickTitle')">
            <v-card
              v-for="p in PROVIDERS"
              :key="p.value"
              class="qa-vs-tile"
              :class="{ 'qa-vs-tile--on': config.sttProvider === p.value }"
              :variant="config.sttProvider === p.value ? 'tonal' : 'outlined'"
              :color="config.sttProvider === p.value ? 'primary' : undefined"
              role="radio"
              :aria-checked="config.sttProvider === p.value"
              link
              @click="config.sttProvider = p.value"
            >
              <div class="d-flex align-center pa-3" style="gap: 12px">
                <v-icon :icon="p.icon" size="28" />
                <div class="flex-grow-1" style="min-width: 0">
                  <div class="text-body-1 font-weight-medium">{{ $t(p.label) }}</div>
                  <div class="text-caption text-medium-emphasis">{{ $t(p.sub) }}</div>
                </div>
                <v-icon :icon="config.sttProvider === p.value ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank'" />
              </div>
            </v-card>
          </div>

          <div class="qa-set-row mt-2">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaVsEnable') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaVsEnableDesc') }}
              </div>
            </div>
            <v-switch v-model="config.enabled" color="primary" density="compact" hide-details inset />
          </div>
        </div>
      </v-card>
    </v-col>

    <v-col cols="12">
      <!-- ผู้อ่านค่าคือ Fusion.Middleware — บอกให้ชัดว่าหน้านี้ไม่ได้คุมโปรเซสตัวเอง
           และค่าที่แก้มีผลกับ "สายถัดไป" ไม่ใช่สายที่กำลังคุยอยู่ (middleware อ่านตอนเปิด socket) -->
      <v-alert type="info" variant="tonal" density="compact" icon="mdi-phone-in-talk-outline">
        <span class="text-body-2">
          {{ $t('qaVsMiddlewareNote') }}
        </span>
      </v-alert>
    </v-col>

    <!-- ② การ์ด Azure | การ์ด Whisper วางคู่กันเสมอ · ตัวที่เลือกเน้นสี ตัวที่ไม่ได้ใช้จางลงแต่ยังแก้ได้
         (กดหัวการ์ดที่จาง = เลือกตัวนั้น) · เส้นทางข้อมูลอยู่ท้ายการ์ดของตัวเอง -->
    <v-col cols="12" md="6" class="d-flex">
      <v-card class="qa-card-sm qa-vs-pcard" :class="isOwn ? 'qa-vs-pcard--off' : 'qa-vs-pcard--on'" variant="outlined">
        <v-card-title class="py-3 qa-vs-pcard__head" :class="{ 'qa-vs-pcard__head--pick': isOwn }" @click="config.sttProvider = 'azure'">
          <v-icon icon="mdi-microsoft-azure" size="small" class="mr-2" />
          <span class="text-subtitle-1">{{ $t('qaVsSttTitle') }}</span>
          <v-spacer />
          <v-chip v-if="!isOwn" color="primary" size="small" variant="flat" prepend-icon="mdi-check">{{ $t('qaVsActive') }}</v-chip>
          <v-chip v-else size="small" variant="outlined">{{ $t('qaVsInactive') }}</v-chip>
        </v-card-title>
        <v-divider />
        <div class="pa-4 qa-vs-pcard__body">
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaVsLang') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaVsLangDesc') }}</div>
            </div>
            <v-text-field v-model="config.language" density="compact" variant="outlined" hide-details placeholder="th-TH" style="max-width: 140px" />
          </div>
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaVsFlush') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaVsFlushDesc') }}
              </div>
            </div>
            <v-text-field v-model.number="config.bufferSeconds" type="number" min="1" max="60" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>
          <!-- ภาพนี้วาดจากค่าที่กำลังพิมพ์ · จุดประสงค์คือให้เห็นว่าเลขตัวเดียวนี้คุมสามอย่าง
               และที่สำคัญกว่านั้นคือเห็นว่า STT **ไม่ได้** ถูกหั่นตามมัน -->
          <div class="qa-set-row qa-set-row--stack">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaVsTimeline') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaVsTimelineDesc', { sec: bufferSec }) }}
              </div>
            </div>

            <div class="qa-vs-timeline">
              <svg viewBox="0 0 560 156" class="qa-vs-timeline__svg" role="img" :aria-label="$t('qaVsTimelineDesc', { sec: bufferSec })">
                <!-- เส้นแบ่งรอบ -->
                <g stroke="#cfd8dc" stroke-width="1" stroke-dasharray="2 3">
                  <line v-for="m in marks" :key="`g${m.i}`" :x1="m.x" y1="20" :x2="m.x" y2="146" />
                </g>
                <g fill="#78909c" font-size="9" text-anchor="middle" font-family="monospace">
                  <text v-for="m in marks" :key="`t${m.i}`" :x="m.x" y="13">{{ m.sec }}</text>
                </g>

                <!-- แถวที่ 1 · เสียงเข้ามาต่อเนื่อง -->
                <text x="0" y="38" fill="#546e7a" font-size="10">{{ $t('qaVsRowAudio') }}</text>
                <rect x="92" y="27" width="456" height="15" rx="2" fill="#eceff1" stroke="#cfd8dc" />

                <!-- แถวที่ 2 · STT ต่อเนื่อง ไม่ถูกหั่น — จุดที่คนเข้าใจผิดบ่อยที่สุด -->
                <text x="0" y="63" fill="#546e7a" font-size="10">{{ $t('qaVsRowStt') }}</text>
                <rect x="92" y="52" width="456" height="15" rx="2" fill="#1976d2" fill-opacity="0.12" stroke="#1976d2" />
                <text x="320" y="63" fill="#1565c0" font-size="9" text-anchor="middle">{{ $t('qaVsRowSttNote') }}</text>

                <!-- แถวที่ 3 · เขียนข้อความลง DB ทุกรอบ -->
                <text x="0" y="92" fill="#546e7a" font-size="10">{{ $t('qaVsRowFlush') }}</text>
                <g stroke="#2e7d32" stroke-width="1.4" fill="none">
                  <line v-for="m in flushMarks" :key="`f${m.i}`" :x1="m.x" y1="74" :x2="m.x" y2="88" />
                </g>
                <g fill="#2e7d32">
                  <polygon v-for="m in flushMarks" :key="`fa${m.i}`" :points="`${m.x - 3},88 ${m.x + 3},88 ${m.x},93`" />
                </g>

                <!-- แถวที่ 4 · คลิปเสียงส่งบริการวิเคราะห์อารมณ์ (เฉพาะโหมด GPU) -->
                <text x="0" y="118" fill="#546e7a" font-size="10">{{ $t('qaVsRowClip') }}</text>
                <g>
                  <rect
                    v-for="c in clips"
                    :key="`c${c.i}`"
                    :x="c.x"
                    y="107"
                    :width="c.width"
                    height="15"
                    rx="2"
                    fill="#f57c00"
                    fill-opacity="0.18"
                    stroke="#f57c00"
                  />
                  <text
                    v-for="c in clips"
                    :key="`ct${c.i}`"
                    :x="c.x + c.width / 2"
                    y="118"
                    fill="#e65100"
                    font-size="8.5"
                    text-anchor="middle"
                    font-family="monospace"
                  >{{ bufferSec }}s</text>
                </g>
                <text x="92" y="140" fill="#90a4ae" font-size="8.5">{{ $t('qaVsRowClipNote') }}</text>
              </svg>

              <div class="text-caption text-medium-emphasis mt-1">{{ $t('qaVsTimelineLegend', { sec: bufferSec }) }}</div>
              <div v-if="bufferSec > CLIP_CEILING" class="text-caption text-warning mt-1">
                {{ $t('qaVsClipCapped', { sec: bufferSec, cap: CLIP_CEILING }) }}
              </div>
            </div>
          </div>

          <v-spacer />
          <!-- read-only cheat sheet — คนที่มาตั้งค่าควรเห็นภาพรวมโดยไม่ต้องเปิดเอกสาร -->
          <div class="qa-vs-path">
            <div class="text-body-2 font-weight-medium mb-1">
              <v-icon icon="mdi-transit-connection-variant" size="small" class="mr-1" />{{ $t('qaVsPathTitle') }}
            </div>
            <div class="text-body-2 mb-1">{{ $t('qaVsPathLine') }}</div>
            <div class="text-caption text-medium-emphasis">{{ $t('qaVsPathNote') }}</div>
          </div>
        </div>
      </v-card>
    </v-col>

    <v-col cols="12" md="6" class="d-flex">
      <v-card class="qa-card-sm qa-vs-pcard" :class="isOwn ? 'qa-vs-pcard--on' : 'qa-vs-pcard--off'" variant="outlined">
        <v-card-title class="py-3 qa-vs-pcard__head" :class="{ 'qa-vs-pcard__head--pick': !isOwn }" @click="config.sttProvider = 'own'">
          <v-icon icon="mdi-server" size="small" class="mr-2" />
          <span class="text-subtitle-1">{{ $t('qaVsWTitle') }}</span>
          <v-spacer />
          <v-chip v-if="isOwn" color="primary" size="small" variant="flat" prepend-icon="mdi-check">{{ $t('qaVsActive') }}</v-chip>
          <v-chip v-else size="small" variant="outlined">{{ $t('qaVsInactive') }}</v-chip>
        </v-card-title>
        <v-divider />
        <div class="pa-4 qa-vs-pcard__body">
          <!-- ⚠️ own ที่ล่ม = ไม่ถอยไป Azure ให้เอง — ต้องบอกตรง ๆ ก่อนคนกดบันทึก (เตือนเฉพาะตอนเลือกอยู่) -->
          <v-alert v-if="isOwn" type="warning" variant="tonal" density="compact" class="mb-2">
            <span class="text-body-2">{{ $t('qaVsOwnWarn') }}</span>
          </v-alert>

          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaVsOwnLang') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaVsOwnLangDesc') }}</div>
            </div>
            <v-btn-toggle v-model="config.ownSttLang" color="primary" density="compact" variant="outlined" divided mandatory>
              <v-btn v-for="l in OWN_LANGS" :key="l" :value="l">{{ $t(`qaVsOwnLang_${l}`) }}</v-btn>
            </v-btn-toggle>
          </div>

          <div class="qa-set-row qa-set-row--stack">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaVsOwnUrl') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaVsOwnUrlDesc') }}</div>
            </div>
            <v-text-field
              v-model="config.ownSttBaseUrl"
              density="compact"
              variant="outlined"
              hide-details="auto"
              clearable
              spellcheck="false"
              :rules="baseUrlRules"
              placeholder="http://stt:8000/v1/stt/utterance"
              prepend-inner-icon="mdi-server-network"
            />
          </div>

          <div v-for="f in OWN_FIELDS" :key="f.key" class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t(f.label) }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t(`${f.label}Desc`) }}</div>
              <div class="text-caption text-medium-emphasis">{{ rangeText(f) }}</div>
            </div>
            <v-text-field
              v-model.number="config[f.key]"
              type="number"
              :min="f.min"
              :max="f.max"
              :step="f.step"
              density="compact"
              variant="outlined"
              hide-details
              style="max-width: 110px"
            />
          </div>

          <!-- ภาพคู่แฝดของ Azure — เสียงถูกตัดเป็นท่อนก่อน แล้วข้อความเกิดทีละท่อน
               ⚠️ schematic: แกนเวลาคงที่ 20 วินาที · ตำแหน่งท่อนพูดตายตัว · ช่วงเงียบกว้างตาม silenceMs (บีบให้อ่านออก) -->
          <div class="qa-set-row qa-set-row--stack">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaVsWTimeline') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaVsWTimelineDesc', { ms: silenceMsView }) }}
              </div>
            </div>

            <div class="qa-vs-timeline">
              <svg viewBox="0 0 560 156" class="qa-vs-timeline__svg" role="img" :aria-label="$t('qaVsWTimelineDesc', { ms: silenceMsView })">
                <!-- เส้นแบ่งเวลา -->
                <g stroke="#cfd8dc" stroke-width="1" stroke-dasharray="2 3">
                  <line v-for="m in wMarks" :key="`wg${m.i}`" :x1="m.x" y1="20" :x2="m.x" y2="146" />
                </g>
                <g fill="#78909c" font-size="9" text-anchor="middle" font-family="monospace">
                  <text v-for="m in wMarks" :key="`wt${m.i}`" :x="m.x" y="13">{{ m.sec }}</text>
                </g>

                <!-- แถวที่ 1 · เสียงเข้ามาต่อเนื่อง (แถบเข้ม = ช่วงที่มีคนพูด) -->
                <text x="0" y="38" fill="#546e7a" font-size="10">{{ $t('qaVsWRowAudio') }}</text>
                <rect x="92" y="27" width="456" height="15" rx="2" fill="#eceff1" stroke="#cfd8dc" />
                <rect v-for="u in utterances" :key="`wa${u.i}`" :x="u.x" y="30" :width="u.width" height="9" rx="1" fill="#b0bec5" />

                <!-- แถวที่ 2 · VAD ตัดเป็นท่อน — เงียบครบ silenceMs = ปิดท่อน (เส้นม่วง) -->
                <text x="0" y="63" fill="#546e7a" font-size="10">{{ $t('qaVsWRowVad') }}</text>
                <g>
                  <rect
                    v-for="u in utterances"
                    :key="`wv${u.i}`"
                    :x="u.x"
                    y="52"
                    :width="u.width"
                    height="15"
                    rx="2"
                    fill="#7b1fa2"
                    fill-opacity="0.14"
                    stroke="#7b1fa2"
                  />
                  <rect
                    v-for="u in utterances"
                    :key="`ws${u.i}`"
                    :x="u.x + u.width"
                    y="55"
                    :width="u.cutX - u.x - u.width"
                    height="9"
                    fill="none"
                    stroke="#9e9e9e"
                    stroke-dasharray="2 2"
                  />
                  <line v-for="u in utterances" :key="`wc${u.i}`" :x1="u.cutX" y1="49" :x2="u.cutX" y2="70" stroke="#7b1fa2" stroke-width="1.6" />
                </g>
                <text :x="utterances[0].x + utterances[0].width - 3" y="80" fill="#6a1b9a" font-size="8.5" text-anchor="end">{{ $t('qaVsWRowVadNote', { ms: silenceMsView }) }}</text>

                <!-- แถวที่ 3 · Whisper ถอดทีละท่อน -->
                <text x="0" y="101" fill="#546e7a" font-size="10">{{ $t('qaVsWRowWhisper') }}</text>
                <g stroke="#1976d2" stroke-width="1.2" fill="none">
                  <line v-for="u in utterances" :key="`wl${u.i}`" :x1="u.cutX" y1="70" :x2="u.cutX" y2="87" />
                </g>
                <g>
                  <rect
                    v-for="u in utterances"
                    :key="`wr${u.i}`"
                    :x="u.cutX"
                    y="90"
                    :width="WHISPER_BLOCK"
                    height="15"
                    rx="2"
                    fill="#1976d2"
                    fill-opacity="0.12"
                    stroke="#1976d2"
                  />
                  <text
                    v-for="u in utterances"
                    :key="`wrt${u.i}`"
                    :x="u.cutX + WHISPER_BLOCK / 2"
                    y="101"
                    fill="#1565c0"
                    font-size="8.5"
                    text-anchor="middle"
                    font-family="monospace"
                  >#{{ u.i + 1 }}</text>
                </g>

                <!-- แถวที่ 4 · บันทึกทีละประโยคที่ถอดได้ (ไม่มีรอบ N วินาทีในภาพนี้) -->
                <text x="0" y="128" fill="#546e7a" font-size="10">{{ $t('qaVsRowFlush') }}</text>
                <g stroke="#2e7d32" stroke-width="1.4" fill="none">
                  <line v-for="u in utterances" :key="`wf${u.i}`" :x1="u.cutX + WHISPER_BLOCK" y1="105" :x2="u.cutX + WHISPER_BLOCK" y2="124" />
                </g>
                <g fill="#2e7d32">
                  <polygon
                    v-for="u in utterances"
                    :key="`wfa${u.i}`"
                    :points="`${u.cutX + WHISPER_BLOCK - 3},124 ${u.cutX + WHISPER_BLOCK + 3},124 ${u.cutX + WHISPER_BLOCK},129`"
                  />
                </g>
                <text x="92" y="144" fill="#2e7d32" font-size="8.5">{{ $t('qaVsWRowFlushNote') }}</text>
              </svg>

              <div class="text-caption text-medium-emphasis mt-1">{{ $t('qaVsWTimelineLegend', { ms: silenceMsView }) }}</div>
              <!-- ⚠️ ความจริงฝั่ง middleware ณ ตอนนี้: VoiceStreamCoordinator ยังเขียนลงฐานตาม BufferSeconds
                   ทั้งสองโหมด และคลิป GPU ก็ยาวตามค่านั้น — บอกไว้ ไม่ให้ภาพด้านบนเข้าใจผิด
                   ถ้า middleware เปลี่ยนเป็นเขียนทันทีต่อประโยคแล้ว ลบบรรทัดนี้ได้ -->
              <div class="text-caption text-medium-emphasis mt-1">{{ $t('qaVsWFlushCaveat', { sec: bufferSec }) }}</div>
            </div>
          </div>

          <!-- ขั้นสูง — ตั้งต้นพับไว้ เพราะค่าตั้งต้นมาจากงานวิจัย ไม่ใช่ค่าที่ควรแตะทุกวัน · ใช้เฉพาะ own -->
          <v-expansion-panels variant="accordion" class="mt-3">
            <v-expansion-panel>
              <v-expansion-panel-title class="text-body-2">
                <v-icon icon="mdi-content-cut" size="small" class="mr-2" />{{ $t('qaVsGateTitle') }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="text-caption text-medium-emphasis mb-2">{{ $t('qaVsGateIntro') }}</div>

                <div class="qa-set-row qa-set-row--stack">
                  <div>
                    <div class="text-body-2 font-weight-medium">{{ $t('qaVsGateProfile') }}</div>
                    <div class="text-caption text-medium-emphasis">{{ $t('qaVsGateProfileDesc') }}</div>
                  </div>
                  <v-btn-toggle v-model="config.gateProfile" color="primary" density="compact" variant="outlined" divided mandatory>
                    <v-btn value="auto" prepend-icon="mdi-auto-fix">{{ $t('qaVsGateProfile_auto') }}</v-btn>
                    <v-btn value="quiet" prepend-icon="mdi-volume-low">{{ $t('qaVsGateProfile_quiet') }}</v-btn>
                    <v-btn value="noisy" prepend-icon="mdi-volume-high">{{ $t('qaVsGateProfile_noisy') }}</v-btn>
                  </v-btn-toggle>
                  <div class="text-caption text-medium-emphasis">{{ $t(`qaVsGateProfileHint_${config.gateProfile || 'auto'}`) }}</div>
                </div>

                <div v-for="f in visibleGateFields" :key="f.key" class="qa-set-row">
                  <div>
                    <div class="text-body-2 font-weight-medium">{{ $t(f.label) }}</div>
                    <div class="text-caption text-medium-emphasis">{{ $t(`${f.label}Desc`) }}</div>
                    <div class="text-caption text-medium-emphasis">{{ rangeText(f) }}</div>
                  </div>
                  <v-text-field
                    v-model.number="config[f.key]"
                    type="number"
                    :min="f.min"
                    :max="f.max"
                    :step="f.step"
                    density="compact"
                    variant="outlined"
                    hide-details
                    style="max-width: 110px"
                  />
                </div>

                <div class="qa-set-row">
                  <div>
                    <div class="text-body-2 font-weight-medium">{{ $t('qaVsGateVad') }}</div>
                    <div class="text-caption text-medium-emphasis">{{ $t('qaVsGateVadDesc') }}</div>
                  </div>
                  <v-btn-toggle v-model="config.vadScorer" color="primary" density="compact" variant="outlined" divided mandatory>
                    <v-btn value="silero">Silero</v-btn>
                    <v-btn value="energy">{{ $t('qaVsGateVad_energy') }}</v-btn>
                  </v-btn-toggle>
                </div>

                <div class="text-body-2 font-weight-medium mt-4">{{ $t('qaVsOnsetTitle') }}</div>
                <div class="text-caption text-medium-emphasis">{{ $t('qaVsOnsetIntro') }}</div>
                <div v-for="f in ONSET_FIELDS" :key="f.key" class="qa-set-row">
                  <div>
                    <div class="text-body-2 font-weight-medium">{{ $t(f.label) }}</div>
                    <div class="text-caption text-medium-emphasis">{{ $t(`${f.label}Desc`) }}</div>
                    <div class="text-caption text-medium-emphasis">{{ rangeText(f) }}</div>
                  </div>
                  <v-text-field
                    v-model.number="config[f.key]"
                    type="number"
                    :min="f.min"
                    :max="f.max"
                    :step="f.step"
                    density="compact"
                    variant="outlined"
                    hide-details
                    style="max-width: 110px"
                  />
                </div>

                <v-btn variant="text" size="small" prepend-icon="mdi-restore" class="mt-2" @click="resetGate">
                  {{ $t('qaVsGateReset') }}
                </v-btn>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-spacer />
          <!-- read-only cheat sheet ของทาง Whisper — คู่กับเส้นทางของ Azure -->
          <div class="qa-vs-path">
            <div class="text-body-2 font-weight-medium mb-1">
              <v-icon icon="mdi-transit-connection-variant" size="small" class="mr-1" />{{ $t('qaVsWPathTitle') }}
            </div>
            <div class="text-body-2 mb-1">{{ $t('qaVsWPathLine') }}</div>
            <div class="text-caption text-medium-emphasis mb-1">{{ $t('qaVsWPathKey') }}</div>
            <div class="text-caption text-medium-emphasis">{{ $t('qaVsPathNote') }}</div>
          </div>
        </div>
      </v-card>
    </v-col>

    <!-- ③ ทดสอบถอดเสียง — ใช้ค่าบนหน้านี้ที่ยังไม่บันทึก (toPayload) ส่งผ่าน CF → Middleware → Azure / Whisper -->
    <v-col cols="12">
      <QaVoiceSttTestCard :default-provider="config.sttProvider" :get-settings="toPayload" />
    </v-col>

    <v-col cols="12">
      <v-btn color="primary" variant="flat" :loading="saving" :disabled="!canSave" prepend-icon="mdi-content-save-outline" @click="save">{{ $t('qaSetSaveConfig') }}</v-btn>
      <span v-if="updatedAt" class="text-caption text-medium-emphasis ml-3">
        {{ $t('qaSetLastEditedBy') }} {{ updatedBy || $t('qaSetUnknown') }} · {{ updatedAtText }}
      </span>
      <span v-else class="text-caption text-medium-emphasis ml-3">{{ $t('qaSetNeverSaved') }}</span>
    </v-col>
  </v-row>
</template>

<script>
/**
 * V1 — แท็บ "เสียง (Voxtron)" (ของจริง · cc.QaSettings['voice-stream'])
 *
 * ⚠️ ผู้อ่านค่าคือ Fusion.Middleware ไม่ใช่ backend ของหน้านี้ — CF เป็นแค่หน้าแก้ค่า
 *    (ดูหัวไฟล์ QaVoiceStreamModels.cs) · มีเฉพาะค่าที่ middleware ใช้จริง
 *    3 ตัวเดิม (enabled / bufferSeconds / language) + ตัวถอดเสียง azure|own และประตูตัดประโยค
 *
 * หน้าตา: การ์ดเลือกตัวถอดเสียง (ไทล์ azure|own + สวิตช์ enabled) อยู่บนสุด
 *   → การ์ด Azure | การ์ด Whisper วางคู่กันเสมอ · ตัวที่เลือกเน้นสี อีกตัวจางแต่ยังแก้ได้ (กดหัวการ์ด = เลือก)
 *   bufferSeconds อยู่ในการ์ด Azure เท่านั้น (ผู้ใช้ขอ) แต่ยังบันทึก/โหลดเหมือนเดิม
 *   ⚠️ middleware ปัจจุบันยังใช้ bufferSeconds เป็นรอบ flush + ความยาวคลิป GPU ในโหมด own ด้วย
 *      (VoiceStreamCoordinator) — การ์ด Whisper จึงบอกไว้ใน qaVsWFlushCaveat
 */
import qaConsole from '@/services/qaConsole';
import QaVoiceSttTestCard from './QaVoiceSttTestCard.vue';

/** จำนวนรอบที่วาดในภาพ — พอให้เห็นจังหวะซ้ำ โดยภาพยังไม่ล้นแถว */
const TIMELINE_ROUNDS = 4;

/** ช่วงแกนเวลาในภาพ (px) — ใช้ร่วมกันทั้งภาพ Azure และ Whisper */
const PLOT_LEFT = 92;
const PLOT_WIDTH = 456;

/**
 * ภาพ Whisper: ท่อนพูดสมมติ [x เริ่ม, x จบ] บนแกน px — ตายตัว เพราะเป็น schematic
 * ⚠️ ช่องว่างระหว่างท่อน (≥ 42px) ต้องกว้างกว่า GAP_MAX + WHISPER_BLOCK ไม่งั้นกล่องข้อความทับท่อนถัดไป
 */
const SPEECH_SPANS = [
  [96, 186],
  [228, 296],
  [338, 374],
  [414, 478],
];
/** ความกว้างช่วงเงียบในภาพ บีบให้อยู่ในช่วงนี้ (ค่าจริงอาจแคบจนมองไม่เห็นเมื่อรอบยาว) */
const GAP_MIN = 6;
const GAP_MAX = 34;
/** กล่อง "ข้อความของท่อน" ในแถว Whisper */
const WHISPER_BLOCK = 26;
/**
 * แกนเวลาของภาพ Whisper (วินาที) — คงที่ ไม่ผูกกับ bufferSeconds (ค่านั้นเป็นของการ์ด Azure)
 * ใช้แค่คิดความกว้างช่วงเงียบจาก silenceMs และติดป้ายวินาที
 */
const W_SPAN_SEC = 20;

/** ไทล์เลือกตัวถอดเสียง — value ต้องตรงกับ VoiceSttProviders ฝั่ง middleware */
const PROVIDERS = [
  { value: 'azure', icon: 'mdi-microsoft-azure', label: 'qaVsProvAzure', sub: 'qaVsProvAzureSub' },
  { value: 'own', icon: 'mdi-server', label: 'qaVsProvOwn', sub: 'qaVsProvOwnSub' },
];

/** ค่าตั้งต้น ใช้ตอนช่องถูกลบจนว่าง (Number('') = 0 ซึ่งทำให้ภาพพัง) */
const BUFFER_DEFAULT = 5;

/**
 * เพดานความยาวคลิปที่บริการวิเคราะห์อารมณ์รับได้ (MAX_AUDIO_SEC)
 *
 * ⚠️ เลขเดียวกับ QaSentimentGpuSettings.MaxClipSecondsCeiling ฝั่ง server ·
 *    ที่นี่ใช้แค่ "เตือน" ไม่ได้บีบค่า เพราะ bufferSeconds คุม transcript ด้วย
 *    ซึ่งตั้งได้ถึง 60 · ตัวที่บีบจริงคือฝั่งที่ตัดคลิป
 */
const CLIP_CEILING = 15;

/**
 * ค่าตั้งต้นของช่องตัวถอดเสียง/ประตูตัดประโยค
 *
 * ⚠️ ต้องตรงกับ QaVoiceStreamSettings (CF) และ VoiceStreamSettings (middleware) ทุกตัว —
 *    ใช้ตอนแถวยังไม่มีช่องนั้น (แถวเก่า) ตอนช่องถูกลบจนว่าง และตอนกด "คืนค่าเริ่มต้น"
 */
const STT_DEFAULTS = Object.freeze({
  sttProvider: 'azure',
  ownSttBaseUrl: '',
  ownSttLang: 'auto',
  ownSttTimeoutSeconds: 15,
  ownSttMaxInFlight: 2,
  silenceMs: 500,
  startDb: 10,
  endDb: 6,
  speechDropDb: 16,
  minVoicedMs: 350,
  maxUtteranceMs: 14000,
  vadScorer: 'silero',
  gateProfile: 'auto',
  snrToNoisyDb: 18,
  snrToQuietDb: 24,
  agentOnsetMs: 300,
  customerYieldMs: 150,
  echoGuardDb: 18,
});

const OWN_LANGS = ['auto', 'th', 'en', 'zh'];

/**
 * ช่องตัวเลข · min/max = ช่วง clamp ฝั่ง server (แสดงเป็นคำอธิบาย + ใส่ใน input)
 * ⚠️ server เป็นตัวบีบค่าจริง หน้าจอแค่บอกช่วง แล้วรับค่าที่บีบแล้วกลับมาแสดงหลังบันทึก
 * int = ฝั่ง server เป็น int — ส่งทศนิยมไปจะโดน model binder ปฏิเสธเป็น 400 จึงปัดก่อนส่ง
 */
const OWN_FIELDS = [
  { key: 'ownSttTimeoutSeconds', label: 'qaVsOwnTimeout', min: 2, max: 60, step: 1, unit: 'sec', int: true },
  { key: 'ownSttMaxInFlight', label: 'qaVsOwnInFlight', min: 1, max: 4, step: 1, unit: '', int: true },
];

const GATE_FIELDS = [
  { key: 'silenceMs', label: 'qaVsGateSilence', min: 200, max: 1800, step: 50, unit: 'ms', int: true },
  { key: 'startDb', label: 'qaVsGateStartDb', min: 0, max: 40, step: 1, unit: 'dB' },
  { key: 'endDb', label: 'qaVsGateEndDb', min: 0, max: 30, step: 1, unit: 'dB' },
  { key: 'speechDropDb', label: 'qaVsGateDropDb', min: 0, max: 40, step: 1, unit: 'dB' },
  { key: 'minVoicedMs', label: 'qaVsGateMinVoiced', min: 100, max: 2000, step: 50, unit: 'ms', int: true },
  { key: 'maxUtteranceMs', label: 'qaVsGateMaxUtt', min: 3000, max: 28000, step: 1000, unit: 'ms', int: true },
  // ใช้เฉพาะโปรไฟล์ auto (เกณฑ์สลับ quiet ⇄ noisy)
  { key: 'snrToNoisyDb', label: 'qaVsGateSnrNoisy', min: 0, max: 60, step: 1, unit: 'dB', autoOnly: true },
  { key: 'snrToQuietDb', label: 'qaVsGateSnrQuiet', min: 0, max: 60, step: 1, unit: 'dB', autoOnly: true },
];

const ONSET_FIELDS = [
  // 0 = ปิดหลักนี้ · ค่าอื่นถูกบีบเป็น 100–2000 (server ไม่ดัน 0 ขึ้นเป็น 100)
  { key: 'agentOnsetMs', label: 'qaVsOnsetMs', min: 0, rangeMin: 100, max: 2000, step: 50, unit: 'ms', int: true, zeroOff: true },
  { key: 'customerYieldMs', label: 'qaVsOnsetYield', min: 32, max: 1000, step: 10, unit: 'ms', int: true },
  { key: 'echoGuardDb', label: 'qaVsOnsetEcho', min: 0, max: 40, step: 1, unit: 'dB' },
];

const NUMBER_FIELDS = [...OWN_FIELDS, ...GATE_FIELDS, ...ONSET_FIELDS];

/**
 * ตรวจ base URL แบบเดียวกับที่ middleware ตรวจตอนเปิดสาย (Uri absolute + http/https)
 * ⚠️ ไม่บังคับ https เหมือนแท็บ GPU — บริการนี้อยู่ในวงใน (เช่น http://stt:8000) ตามเอกสาร middleware
 *    แต่ห้าม user:pass@ / ?query / #hash — ค่านี้ถูกเก็บ plain text ที่ role "2" อ่านได้
 */
function isValidBaseUrl(value) {
  const v = (value || '').trim();
  if (!v) return false;
  try {
    const u = new URL(v);
    return (u.protocol === 'http:' || u.protocol === 'https:') && !u.username && !u.password && !u.search && !u.hash;
  } catch {
    return false;
  }
}

export default {
  name: 'QaVoiceStreamSettings',
  components: { QaVoiceSttTestCard },
  emits: ['saved'],
  data() {
    return {
      // ค่าตั้งต้นตรงกับ QaVoiceStreamSettings.Default ฝั่ง server
      config: {
        enabled: true,
        bufferSeconds: 5,
        language: 'th-TH',
        ...STT_DEFAULTS,
      },
      updatedAt: null,
      updatedBy: null,
      saving: false,
    };
  },
  computed: {
    CLIP_CEILING: () => CLIP_CEILING,
    WHISPER_BLOCK: () => WHISPER_BLOCK,
    PROVIDERS: () => PROVIDERS,
    OWN_LANGS: () => OWN_LANGS,
    OWN_FIELDS: () => OWN_FIELDS,
    ONSET_FIELDS: () => ONSET_FIELDS,

    isOwn() {
      return this.config.sttProvider === 'own';
    },

    /** เกณฑ์ SNR มีผลเฉพาะโปรไฟล์ auto — โปรไฟล์อื่นซ่อนไว้ ไม่ให้คนแก้แล้วงงว่าทำไมไม่มีผล */
    visibleGateFields() {
      return GATE_FIELDS.filter((f) => !f.autoOnly || this.config.gateProfile === 'auto');
    },

    baseUrlRules() {
      return [(v) => isValidBaseUrl(v) || this.$t('qaVsOwnUrlInvalid')];
    },

    /**
     * own + base URL ใช้ไม่ได้ = middleware ปฏิเสธทุกสาย (503) — ไม่ให้บันทึกสถานะนั้นลงไป
     * (azure ไม่สนช่องนี้ จึงบันทึกได้เสมอ)
     */
    canSave() {
      return !this.isOwn || isValidBaseUrl(this.config.ownSttBaseUrl);
    },

    /** ค่าที่ใช้วาดภาพ — ช่องว่าง/0/ติดลบ ตกกลับไปที่ค่าตั้งต้นเสมอ */
    bufferSec() {
      return Math.max(1, Number(this.config.bufferSeconds) || BUFFER_DEFAULT);
    },

    /**
     * ภาพเป็นแบบ schematic — แบ่งเท่า ๆ กัน TIMELINE_ROUNDS ช่วง แล้วติดป้ายด้วยวินาทีจริง
     * ⚠️ ตั้งใจไม่ scale ตามวินาที เพราะ N ตั้งได้ถึง 60 · ถ้า scale จริง ภาพจะแบนจนอ่านไม่ออก
     */
    marks() {
      const out = [];
      for (let i = 0; i <= TIMELINE_ROUNDS; i += 1) {
        out.push({ i, x: PLOT_LEFT + (PLOT_WIDTH / TIMELINE_ROUNDS) * i, sec: i * this.bufferSec });
      }
      return out;
    },

    /** ป้ายวินาทีของภาพ Whisper — แบ่ง 4 ช่วงเท่ากันบนแกนคงที่ */
    wMarks() {
      const out = [];
      for (let i = 0; i <= TIMELINE_ROUNDS; i += 1) {
        out.push({ i, x: PLOT_LEFT + (PLOT_WIDTH / TIMELINE_ROUNDS) * i, sec: (W_SPAN_SEC / TIMELINE_ROUNDS) * i });
      }
      return out;
    },

    /** silenceMs ที่ใช้แสดง — ช่องว่าง/นอกช่วง ถอยไปค่าตั้งต้น/ขอบช่วงเดียวกับ server */
    silenceMsView() {
      const n = Number(this.config.silenceMs);
      if (!Number.isFinite(n) || n <= 0) return STT_DEFAULTS.silenceMs;
      return Math.min(1800, Math.max(200, Math.round(n)));
    },

    /**
     * ท่อนพูดในภาพ Whisper · cutX = จุดที่ middleware ปิดท่อน (หลังเงียบครบ silenceMs) แล้วส่งไปถอด
     * ความกว้างช่วงเงียบคิดจากสเกลจริงของแกน (W_SPAN_SEC) แล้วบีบ GAP_MIN–GAP_MAX
     */
    utterances() {
      const pxPerSec = PLOT_WIDTH / W_SPAN_SEC;
      const gap = Math.min(GAP_MAX, Math.max(GAP_MIN, (this.silenceMsView / 1000) * pxPerSec));
      return SPEECH_SPANS.map(([start, end], i) => ({ i, x: start, width: end - start, cutX: end + gap }));
    },

    /** ลูกศรลง = จังหวะที่ข้อความถูกเขียนลง cc.QaTranscriptLines (ท้ายรอบ ไม่ใช่ต้นรอบ) */
    flushMarks() {
      return this.marks.filter((m) => m.i > 0);
    },

    clips() {
      const width = PLOT_WIDTH / TIMELINE_ROUNDS;
      return this.marks.slice(0, TIMELINE_ROUNDS).map((m) => ({ i: m.i, x: m.x, width: width - 3 }));
    },

    updatedAtText() {
      if (!this.updatedAt) return '';
      try {
        return new Date(this.updatedAt).toLocaleString('th-TH');
      } catch {
        return this.updatedAt;
      }
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    /** "ค่าเริ่มต้น 500 ms · ช่วง 200–1800" */
    rangeText(f) {
      const unit = f.unit === 'sec' ? this.$t('qaVsUnitSec') : f.unit;
      return this.$t(f.zeroOff ? 'qaVsDefaultRangeZeroOff' : 'qaVsDefaultRange', {
        def: STT_DEFAULTS[f.key],
        min: f.rangeMin ?? f.min,
        max: f.max,
        unit: unit ? ` ${unit}` : '',
      });
    },

    resetGate() {
      [...GATE_FIELDS, ...ONSET_FIELDS].forEach((f) => {
        this.config[f.key] = STT_DEFAULTS[f.key];
      });
      this.config.gateProfile = STT_DEFAULTS.gateProfile;
      this.config.vadScorer = STT_DEFAULTS.vadScorer;
    },

    /**
     * ปั้น payload ใหม่ ไม่ส่ง this.config ดิบ ๆ (เหตุผลเดียวกับแท็บ sentiment):
     * ช่อง v-model.number ที่ถูกลบจนว่างเป็นสตริง '' และช่อง int ที่มีทศนิยม — model binder
     * ฝั่ง server ปฏิเสธทั้งก้อนเป็น 400 · ช่องว่างจึงถอยไปค่าตั้งต้น ช่อง int ถูกปัด
     */
    toPayload() {
      const payload = { ...this.config };
      payload.bufferSeconds = Math.round(Number(this.config.bufferSeconds)) || BUFFER_DEFAULT;
      payload.ownSttBaseUrl = (this.config.ownSttBaseUrl || '').trim();
      NUMBER_FIELDS.forEach((f) => {
        const raw = this.config[f.key];
        let n = raw === '' || raw === null || raw === undefined ? NaN : Number(raw);
        if (!Number.isFinite(n)) n = STT_DEFAULTS[f.key];
        payload[f.key] = f.int ? Math.round(n) : n;
      });
      return payload;
    },

    async load() {
      try {
        const res = await qaConsole.getVoiceStreamConfig();
        if (res && res.value) this.config = { ...this.config, ...res.value };
        this.updatedAt = res ? res.updatedAt : null;
        this.updatedBy = res ? res.updatedBy : null;
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaEvalLoadFailed', { msg: err.message }) });
      }
    },
    async save() {
      this.saving = true;
      try {
        const res = await qaConsole.saveVoiceStreamConfig(this.toPayload());
        // รับค่าที่ server คืน (ผ่าน clamp แล้ว) กลับมาแสดง — ไม่ใช่ค่าที่พิมพ์
        if (res && res.value) this.config = { ...this.config, ...res.value };
        this.updatedAt = res ? res.updatedAt : null;
        this.updatedBy = res ? res.updatedBy : null;
        this.$emit('saved', { ok: true, message: this.$t('qaVsSaved') });
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaSetSaveFailed', { msg: err.message }) });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.qa-vs-timeline {
  width: 100%;
  min-width: 0;
}
.qa-vs-timeline__svg {
  display: block;
  width: 100%;
  height: auto;
  max-width: 620px;
}
.qa-vs-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
}
.qa-vs-tile--on {
  border: 2px solid rgb(var(--v-theme-primary)) !important;
}
/* การ์ดคู่ Azure | Whisper — สูงเท่ากัน (v-col d-flex) · เส้นทางชิดท้ายการ์ด */
.qa-vs-pcard {
  display: flex;
  flex-direction: column;
  width: 100%;
  transition: opacity 0.15s ease, border-color 0.15s ease;
}
.qa-vs-pcard--on {
  border: 2px solid rgb(var(--v-theme-primary)) !important;
}
.qa-vs-pcard--off .qa-vs-pcard__body,
.qa-vs-pcard--off .qa-vs-pcard__head {
  opacity: 0.55;
}
.qa-vs-pcard--off:hover .qa-vs-pcard__body,
.qa-vs-pcard--off:focus-within .qa-vs-pcard__body,
.qa-vs-pcard--off:hover .qa-vs-pcard__head {
  opacity: 0.85;
}
.qa-vs-pcard__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.qa-vs-pcard__head--pick {
  cursor: pointer;
}
.qa-vs-pcard__body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
}
.qa-vs-path {
  margin-top: 16px;
  padding: 12px;
  border-radius: 6px;
  background: rgba(var(--v-theme-on-surface), 0.04);
}
.qa-set-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f5;
}
.qa-set-row--stack {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
.qa-set-row:last-of-type {
  border-bottom: none;
}
.qa-set-row > div:first-child {
  flex: 1;
  min-width: 0;
}
</style>
